import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Grid curado para el contexto de reconocimiento (celebración, caras, corazones, gestos/trabajo).
// Sin librería externa ni categorías interactivas — un solo grid con scroll si hace falta.
const RECOGNITION_EMOJIS: readonly string[] = [
  '🎉', '🎊', '✨', '🌟', '⭐', '🏆', '🥇', '🥳', '🚀', '🔥', '💯', '🙌', '👏', '🎯',
  '😀', '😄', '😁', '😊', '🙂', '😉', '😍', '🤩', '😎', '🤗', '🥰', '😇', '😅', '🤓',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🤍', '🖤', '🤎', '💖', '💝',
  '👍', '👌', '🤝', '🙏', '💪', '✍️', '📈', '💡', '🏅', '🎖️', '⚡', '✅',
];

interface EmojiPickerButtonProps {
  /** Emoji elegido por el usuario (glifo Unicode, ej. '🎉'). */
  onSelect: (emoji: string) => void;
  className?: string;
}

/**
 * Botón que abre un selector de emojis (grid curado, sin librería externa). Mismo patrón de
 * popover + cierre al hacer clic afuera que `LanguageMenu`.
 */
export function EmojiPickerButton({ onSelect, className }: Readonly<EmojiPickerButtonProps>) {
  const { t } = useTranslation('recognition');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  function handleSelect(emoji: string) {
    onSelect(emoji);
    setOpen(false);
  }

  return (
    <div className={`emoji-picker ${className ?? ''}`} ref={containerRef}>
      <button
        type="button"
        className="emoji-picker-trigger"
        aria-label={t('common.insertEmoji')}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <i className="far fa-smile" aria-hidden="true" />
      </button>

      {open && (
        <div className="emoji-picker-popover" role="menu">
          <div className="emoji-picker-grid">
            {RECOGNITION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                role="menuitem"
                className="emoji-picker-item"
                aria-label={emoji}
                onClick={() => handleSelect(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default EmojiPickerButton;
